import React from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import SydButton from '../../../../commonDesign/Button'
import { useSeCodes } from './SeDebugContext'

function MarkTaskButton ({ taskId, currentlyCompleted, onSuccess, children, ...buttonProps }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const codes = useSeCodes()

  const targetState = !currentlyCompleted

  const { mutate, isLoading, error } = useMutation({
    mutationFn: () => postNamedCommand('coreData', 'invoke-smart-endpoint', {
      code: codes.mark,
      args: { clientId, taskId, completed: targetState }
    }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.woSfTasks] })

      const cacheKey = [QUERY_KEYS.woSfTasks, clientId, codes.list]
      const previousData = queryClient.getQueryData(cacheKey)

      queryClient.setQueryData(cacheKey, old => {
        if (!old?.tasks) return old
        return {
          ...old,
          tasks: old.tasks.map(t =>
            t.id === taskId ? { ...t, clientMarkedCompleted: targetState } : t
          )
        }
      })

      return { previousData }
    },
    onSuccess: () => {
      if (onSuccess) onSuccess()
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([QUERY_KEYS.woSfTasks, clientId, codes.list], context.previousData)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.woSfTasks] })
      queryClient.invalidateQueries({ queryKey: ['status-report-supplemental', 'smartEndpoint'] })
    }
  })

  return (
    <>
      <SydButton
        {...buttonProps}
        icon={targetState ? 'check' : 'radioUnchecked'}
        processing={isLoading}
        onClick={() => mutate()}
      >
        {children || (targetState ? 'Mark Complete' : 'Mark Incomplete')}
      </SydButton>
      {error ? (
        <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)', marginTop: '4px' }}>
          {error.message || 'Failed to update task'}
        </div>
      ) : null}
    </>
  )
}

MarkTaskButton.propTypes = {
  taskId: PropTypes.string.isRequired,
  currentlyCompleted: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func,
  children: PropTypes.node
}

export default MarkTaskButton
